import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import POASection from './POASection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, Star } from 'lucide-react';

const SERVICE_TYPES = [
  'Cleaning',
  'Landscaping',
  'Facility Maintenance',
  'Security',
  'Porter',
  'Parking Garage',
  'Pressure Washing',
  'Other'
];

const InspectionForm = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;

  const [loading, setLoading] = useState(false);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [poaData, setPoaData] = useState({
    deficiency_description: '',
    corrective_action: '',
    responsible_person: '',
    estimated_correction_date: '',
    photos: [],
    opportunity_for_improvement: ''
  });
  const [formData, setFormData] = useState({
    service_location_id: editData?.service_location_id || '',
    service_type: editData?.service_type || 'Cleaning',
    inspector_name: editData?.inspector_name || user?.name || '',
    inspection_date: editData?.inspection_date || new Date().toISOString().split('T')[0],
    cleanliness_rating: editData?.cleanliness_rating || 3,
    notes: editData?.notes || '',
    issues_found: editData?.issues_found || '',
    corrective_actions: editData?.corrective_actions || ''
  });

  useEffect(() => {
    fetchServiceLocations();
  }, []);

  const fetchServiceLocations = async () => {
    try {
      const response = await axios.get(`${API}/service-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch service locations:', error);
      toast.error('Failed to load service locations');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.service_location_id) {
      toast.error('Please select a service location');
      return;
    }

    // Validate POA for ratings 1-2
    const rating = formData.cleanliness_rating;
    if (rating <= 2) {
      if (!poaData.deficiency_description || !poaData.corrective_action || 
          !poaData.responsible_person || !poaData.estimated_correction_date) {
        toast.error('Plan of Action is MANDATORY for ratings 1-2. Please complete all required fields.');
        return;
      }
      if (!poaData.photos || poaData.photos.length === 0) {
        toast.error('At least one photo is required for deficiencies (ratings 1-2).');
        return;
      }
    }

    setLoading(true);

    try {
      // Get the selected location to extract customer_id
      const selectedLocation = serviceLocations.find(loc => loc.id === formData.service_location_id);
      
      const payload = {
        ...formData,
        customer_id: selectedLocation?.customer_id || '',
        area_type: formData.service_type,
        inspection_items: [],
        poa_items: rating <= 2 ? [poaData] : [],
        deficiencies: rating <= 2 ? [{
          inspection_point: 'Overall Inspection',
          rating: rating,
          description: poaData.deficiency_description,
          photos: poaData.photos,
          requires_poa: true,
          poa: poaData
        }] : []
      };

      if (editData?.id) {
        await axios.put(`${API}/cleaning-inspections/${editData.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Inspection updated successfully!');
      } else {
        await axios.post(`${API}/cleaning-inspections`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Inspection submitted successfully!');
      }
      
      navigate('/field/inspections/list');
    } catch (error) {
      console.error('Submission error:', error);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.errors) {
        toast.error(`Validation failed: ${errorDetail.errors.join(', ')}`);
      } else {
        toast.error(errorDetail || 'Failed to submit inspection');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[rating] || '';
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <ClipboardCheck className="h-8 w-8" style={{ color: '#6B9F3E' }} />
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {editData ? 'Edit Inspection' : 'New Inspection'} / {editData ? 'Editar Inspección' : 'Nueva Inspección'}
          </h2>
        </div>
        <p className="text-sm text-slate-600">
          Submit an inspection report / Enviar un informe de inspección
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Inspection Details / Detalles de Inspección</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Service Location */}
            <div>
              <Label htmlFor="service_location_id">
                Service Location / Ubicación del Servicio <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.service_location_id} 
                onValueChange={(value) => setFormData({...formData, service_location_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location / Seleccionar ubicación" />
                </SelectTrigger>
                <SelectContent>
                  {serviceLocations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.location_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service Type */}
            <div>
              <Label htmlFor="service_type">
                Service Type / Tipo de Servicio <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.service_type} 
                onValueChange={(value) => setFormData({...formData, service_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inspector Name */}
            <div>
              <Label htmlFor="inspector_name">
                Inspector Name / Nombre del Inspector <span className="text-red-500">*</span>
              </Label>
              <Input
                id="inspector_name"
                value={formData.inspector_name}
                onChange={(e) => setFormData({...formData, inspector_name: e.target.value})}
                required
              />
            </div>

            {/* Inspection Date */}
            <div>
              <Label htmlFor="inspection_date">
                Date & Time / Fecha y Hora <span className="text-red-500">*</span>
              </Label>
              <Input
                id="inspection_date"
                type="date"
                value={formData.inspection_date}
                onChange={(e) => setFormData({...formData, inspection_date: e.target.value})}
                required
              />
            </div>

            {/* Rating */}
            <div>
              <Label htmlFor="rating">
                Rating / Calificación (1-5) <span className="text-red-500">*</span>
              </Label>
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData({...formData, cleanliness_rating: rating})}
                      className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                        formData.cleanliness_rating === rating
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Star 
                        className={`h-6 w-6 ${formData.cleanliness_rating === rating ? 'text-green-600' : 'text-gray-400'}`}
                        fill={formData.cleanliness_rating === rating ? 'currentColor' : 'none'}
                      />
                      <span className={`text-sm mt-1 ${formData.cleanliness_rating === rating ? 'font-semibold text-green-600' : 'text-gray-600'}`}>
                        {rating}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-semibold">{formData.cleanliness_rating} - {getRatingLabel(formData.cleanliness_rating)}</span>
                </p>
              </div>
            </div>

            {/* POA Section - Dynamic based on rating */}
            <POASection 
              rating={formData.cleanliness_rating}
              poaData={poaData}
              onPOAChange={setPoaData}
              inspectionPoint="Overall Inspection"
              token={token}
            />

            {/* Notes / Comments */}
            <div>
              <Label htmlFor="notes">
                Notes / Comments / Notas / Comentarios
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Enter any general notes or comments..."
                rows={3}
              />
            </div>

            {/* Issues Found (Optional) */}
            <div>
              <Label htmlFor="issues_found">
                Issues Found / Problemas Encontrados (Optional)
              </Label>
              <Textarea
                id="issues_found"
                value={formData.issues_found}
                onChange={(e) => setFormData({...formData, issues_found: e.target.value})}
                placeholder="Describe any issues or deficiencies found..."
                rows={3}
              />
            </div>

            {/* Corrective Actions (Optional) */}
            <div>
              <Label htmlFor="corrective_actions">
                Corrective Actions / Acciones Correctivas (Optional)
              </Label>
              <Textarea
                id="corrective_actions"
                value={formData.corrective_actions}
                onChange={(e) => setFormData({...formData, corrective_actions: e.target.value})}
                placeholder="Describe any corrective actions taken or recommended..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/field/inspections/list')}
            className="flex-1"
          >
            Cancel / Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1"
            style={{ backgroundColor: '#6B9F3E' }}
          >
            {loading ? 'Submitting... / Enviando...' : (editData ? 'Update / Actualizar' : 'Submit Inspection / Enviar Inspección')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InspectionForm;
